import { useEffect, useMemo, useRef, useState } from "react";

type UseSavedStateOptions = {
  ttlMs?: number; // default 0 = no expiry
  prefix?: string; // default saved
};

// internal shape stored in localStorage
type Box<T> = { v: T; e: number }; // value and expiry epoch ms (e = 0 means no expiry)

const DEFAULT_TTL = 0; // 0 = no expiry
const DEFAULT_PREFIX = "saved";

// avoid crashing on SSR
const safeStorage: Storage | null =
  typeof window !== "undefined" && typeof window.localStorage !== "undefined"
    ? window.localStorage
    : null;

// run cleanup once per app load
let cleanedOnce = false;

function cleanupExpired(prefix: string) {
  if (!safeStorage) return;
  const now = Date.now();
  const tag = prefix + ":";
  const toDelete: string[] = [];
  for (let i = 0; i < safeStorage.length; i++) {
    const k = safeStorage.key(i);
    if (!k || !k.startsWith(tag)) continue;
    try {
      const raw = safeStorage.getItem(k);
      if (!raw) continue;
      const box = JSON.parse(raw) as Box<unknown>;
      // e = 0 means no expiry, otherwise check if expired
      if (typeof box?.e !== "number" || (box.e !== 0 && now >= box.e)) {
        toDelete.push(k);
      }
    } catch {
      toDelete.push(k);
    }
  }
  toDelete.forEach((k) => safeStorage.removeItem(k));
}

function makeKey(prefix: string, key: string) {
  return `${prefix}:${key}`;
}

function readBox<T>(lsKey: string): T | null {
  if (!safeStorage) return null;
  try {
    const raw = safeStorage.getItem(lsKey);
    if (!raw) return null;
    const box = JSON.parse(raw) as Box<T>;
    if (typeof box?.e !== "number") return null;
    // e = 0 means no expiry, otherwise check if expired
    if (box.e !== 0 && Date.now() >= box.e) {
      safeStorage.removeItem(lsKey);
      return null;
    }
    return box.v as T;
  } catch {
    safeStorage?.removeItem(lsKey);
    return null;
  }
}

function writeBox<T>(lsKey: string, value: T, ttlMs: number) {
  if (!safeStorage) return;
  // ttlMs = 0 means no expiry (store e = 0)
  const box: Box<T> = { v: value, e: ttlMs === 0 ? 0 : Date.now() + ttlMs };
  try {
    safeStorage.setItem(lsKey, JSON.stringify(box));
  } catch {
    // storage full or blocked
  }
}

export function useSavedState<T>(
  key: string,
  initial: T,
  options?: UseSavedStateOptions
): [
  T,
  (next: T | ((prev: T) => T), ttlOverrideMs?: number) => void,
  () => void
] {
  const prefix = options?.prefix ?? DEFAULT_PREFIX;
  const ttlMsDefault = options?.ttlMs ?? DEFAULT_TTL;
  const lsKey = useMemo(() => makeKey(prefix, key), [prefix, key]);

  // ensure global cleanup on first mount anywhere
  const cleanedRef = useRef(false);
  useEffect(() => {
    if (!cleanedOnce) {
      cleanupExpired(prefix);
      cleanedOnce = true;
      cleanedRef.current = true;
    }
  }, [prefix]);

  const [state, setState] = useState<T>(() => {
    const stored = readBox<T>(lsKey);
    return stored ?? initial;
  });

  // write on change from this tab
  const setSaved = (next: T | ((prev: T) => T), ttlOverrideMs?: number) => {
    setState((prev) => {
      const value =
        typeof next === "function" ? (next as (p: T) => T)(prev) : next;

      writeBox(lsKey, value, ttlOverrideMs ?? ttlMsDefault);
      return value;
    });
  };

  // react to external changes from other tabs
  useEffect(() => {
    if (!safeStorage) return;
    const onStorage = (ev: StorageEvent) => {
      if (ev.storageArea !== safeStorage) return;
      if (ev.key !== lsKey) return;
      const v = readBox<T>(lsKey);
      if (v === null) {
        // expired or cleared elsewhere
        setState(initial);
      } else {
        setState(v);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [lsKey, initial]);

  // expose a clear helper
  const clear = () => {
    if (safeStorage) safeStorage.removeItem(lsKey);
    setState(initial);
  };

  return [state, setSaved, clear];
}
